import { Outlet, NavLink, useLocation, Link } from "react-router-dom";
import { ArrowRight, LogOut, ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getYouTubeThumb, resourceHref, type ResourceRow } from "@/lib/api/resources";
import { imgUrl } from "@/lib/img";
import { Button } from "@/components/ds";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { LoginModal } from "@/components/portal/LoginModal";
import { UserNotificationsPanel } from "@/components/portal/UserNotificationsPanel";
import Footer from "@/components/portal/Footer";
import { ThemeToggle } from "@/components/portal/ThemeToggle";
import { HeaderSearch } from "@/components/portal/HeaderSearch";
import { fetchSettings, readSettingsSync } from "@/lib/site-settings";
import logcomexLogo from "@/assets/logcomex-principal.svg";
import logcomexAiIcon from "@/assets/logcomex-logotipo-ai-2.svg";
import logcomexAi3 from "@/assets/logcomex-logotipo-ai-3.svg";
import logcomexAi4 from "@/assets/logcomex-logotipo-ai-4.svg";

const PRIMARY_NAV: { to: string; label: string }[] = [];


type HeaderNavItem = { to: string; label: string; external?: boolean };

const DEFAULT_SECONDARY_NAV: HeaderNavItem[] = [
  { to: "/", label: "Blog" },
  { to: "/insights", label: "Insights" },
  { to: "/learn", label: "Learn" },
  { to: "/materiais", label: "Materiais" },
  { to: "/cases", label: "Cases" },
  { to: "/tools", label: "Tools" },
  { to: "/eventos", label: "Eventos" },
  { to: "/recursos", label: "Recursos" },
  { to: "/glossario", label: "Glossário" },
];

const LANGUAGES = [
  { code: "pt", label: "Português", flag: "br" },
  { code: "en", label: "English", flag: "us" },
  { code: "es", label: "Español", flag: "mx" },
  { code: "zh", label: "中文", flag: "cn" },
];

function Flag({ code, size = 20 }: { code: string; size?: number }) {
  return (
    <img
      src={`https://hatscripts.github.io/circle-flags/flags/${code}.svg`}
      alt=""
      width={size}
      height={size}
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => {
    if (typeof window === "undefined") return LANGUAGES[0];
    const code = localStorage.getItem("app_lang");
    return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0];
  });
  return (
    <div className="relative inline-flex items-center">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        aria-label="Alterar idioma"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center justify-center gap-1.5 h-7 px-1 text-[13px] font-medium text-foreground/60 hover:text-primary transition-colors"
      >
        <Flag code={current.flag} size={18} />
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 min-w-[160px] bg-card border border-foreground/10 rounded-lg shadow-lg py-1 z-50">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onMouseDown={() => {
                setCurrent(l);
                setOpen(false);
                try { localStorage.setItem("app_lang", l.code); } catch {}
                window.dispatchEvent(new CustomEvent("app:lang-change", { detail: l.code }));
              }}
              className="w-full px-3 py-1.5 text-left text-[13px] text-foreground/70 hover:bg-muted hover:text-foreground inline-flex items-center gap-2"
            >
              <Flag code={l.flag} size={18} />
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const RESOURCE_LINKS = [
  { to: "/recursos", label: "Todos os recursos" },
  { to: "/recursos/webinars", label: "Webinars" },
  { to: "/recursos/comextech2026", label: "Comex Tech Forum 2026" },
  { to: "/recursos/comextech2025", label: "Comex Tech Forum 2025" },
  { to: "/recursos/use-cases", label: "Casos de uso" },
  { to: "/recursos/ebooks", label: "eBooks" },
  { to: "/recursos/one-pages", label: "One Pages" },
];

const MENU_FORMATS: { format: string; to: string; label: string }[] = [
  { format: "relatorio", to: "/recursos/relatorios", label: "Relatórios" },
  { format: "guia", to: "/recursos/guias", label: "Guias" },
  { format: "webinar", to: "/recursos/webinars", label: "Webinars" },
  { format: "ctf26", to: "/recursos/comextech2026", label: "Comex Tech Forum" },
  { format: "use-case", to: "/recursos/use-cases", label: "Casos de uso" },
  { format: "ebook", to: "/recursos/ebooks", label: "eBooks" },
  { format: "one-page", to: "/recursos/one-pages", label: "One Pages" },
];

function ResourcesMenu({ label, active }: { label: string; active: boolean }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 250);
  };
  useEffect(() => () => cancelClose(), []);
  const { data } = useQuery({
    queryKey: ["resources-menu-latest-v2"],
    queryFn: async () => {
      const { data: resources, error } = await (supabase as any)
        .from("resources")
        .select("*")
        .eq("published", true)
        .in("format", MENU_FORMATS.map((item) => item.format))
        .order("resource_date", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (resources ?? []) as ResourceRow[];
    },
    enabled: open,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const items: ResourceRow[] = data ?? [];

  const latestBy = (format: string) =>
    items
      .filter((r) => format === "ctf26"
        ? r.format === "ctf26" && r.subfolder === "comextech2026"
        : r.format === format)
      .sort((a, b) => (b.resource_date || b.created_at || "").localeCompare(a.resource_date || a.created_at || ""))
      .slice(0, 1);

  return (
    <div
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <Link
        to="/recursos"
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-1 text-[13px] font-medium transition-colors whitespace-nowrap",
          active ? "text-primary" : "text-foreground/65 hover:text-primary",
        )}
      >
        {label}
        <ChevronDown className={cn("w-3 h-3 transition-transform", open && "rotate-180")} />
      </Link>
      {open && (
        <div
          className="absolute left-8 right-8 top-full pt-2 z-50"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-card border border-foreground/10 rounded-2xl shadow-2xl p-5">
            <div className="grid grid-cols-4 xl:grid-cols-7 gap-x-5 gap-y-5">


              {MENU_FORMATS.map((f) => {
                const r = latestBy(f.format)[0];
                const thumb = r ? r.cover_image || getYouTubeThumb(r.youtube) : null;
                return (
                  <div key={f.to} className="flex flex-col">
                    <Link
                      to={f.to}
                      onClick={() => setOpen(false)}
                      className="mb-2 inline-block text-[11px] font-semibold uppercase tracking-[1.4px] text-primary hover:underline"
                    >
                      {f.label}
                    </Link>
                    {!r ? (
                      <p className="text-[12px] text-foreground/70">Em breve</p>
                    ) : (
                      <Link
                        to={resourceHref(r)}
                        onClick={() => setOpen(false)}
                        className="group flex flex-col gap-2 rounded-lg p-1.5 -mx-1.5 hover:bg-muted transition-colors"
                      >
                        <div className="aspect-video w-full overflow-hidden rounded-md bg-muted">
                          {thumb ? (
                            <img
                              src={imgUrl(thumb, 640)}
                              alt={`Capa: ${r.title}`}
                              width={320}
                              height={180}
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div
                              className="h-full w-full"
                              style={{
                                background: `radial-gradient(120% 90% at 0% 0%, hsl(${r.cover_accent || "265 75% 55%"} / 0.55), transparent 60%), hsl(var(--card))`,
                              }}
                            />
                          )}
                        </div>
                        <span className="text-[12.5px] leading-snug text-foreground/75 group-hover:text-primary">
                          {r.title}
                        </span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 border-t border-foreground/[0.08] pt-3">
              <Link
                to="/recursos"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:gap-2.5 transition-all"
              >
                Todos os recursos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}


export default function PortalLayout() {
  const { pathname } = useLocation();
  const { session, signOut } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const initialHeader = readSettingsSync<any>("header");
  const [headerCta, setHeaderCta] = useState<{ label: string; url: string; external: boolean }>({
    label: initialHeader?.cta_label || "Começar grátis",
    url: initialHeader?.cta_url || "https://logcomex.ai",
    external: initialHeader?.cta_external !== false,
  });
  const [logoOverride, setLogoOverride] = useState<{ light: string; dark: string }>({
    light: initialHeader?.logo_url || "",
    dark: initialHeader?.logo_url_dark || "",
  });
  const [tagline, setTagline] = useState<string>(
    typeof initialHeader?.tagline === "string" ? initialHeader.tagline : "Blog",
  );
  const [secondaryNav, setSecondaryNav] = useState<HeaderNavItem[]>(() =>
    Array.isArray(initialHeader?.nav_links) && initialHeader.nav_links.length > 0
      ? initialHeader.nav_links.map((l: any) => ({ to: l.href, label: l.label, external: !!l.external }))
      : DEFAULT_SECONDARY_NAV,
  );
  const [hiddenSections, setHiddenSections] = useState<string[]>(
    Array.isArray(initialHeader?.hidden_sections)
      ? initialHeader.hidden_sections
      : ["/eventos", "/learn", "/materiais"],
  );
  const [showLang, setShowLang] = useState(initialHeader?.show_language_switcher !== false);
  const [showTheme, setShowTheme] = useState(initialHeader?.show_theme_toggle !== false);

  useEffect(() => {
    fetchSettings<any>("header").then((v) => {
      if (!v) return;
      setHeaderCta({
        label: v.cta_label || "Começar grátis",
        url: v.cta_url || "https://logcomex.ai",
        external: v.cta_external !== false,
      });
      setLogoOverride({ light: v.logo_url || "", dark: v.logo_url_dark || "" });
      if (typeof v.tagline === "string") setTagline(v.tagline);
      if (Array.isArray(v.nav_links) && v.nav_links.length > 0) {
        setSecondaryNav(
          v.nav_links.map((l: any) => ({ to: l.href, label: l.label, external: !!l.external })),
        );
      }
      if (Array.isArray(v.hidden_sections)) setHiddenSections(v.hidden_sections);
      if (typeof v.show_language_switcher === "boolean") setShowLang(v.show_language_switcher);
      if (typeof v.show_theme_toggle === "boolean") setShowTheme(v.show_theme_toggle);
    });
  }, []);


  const visibleNav = secondaryNav.filter((n) => !hiddenSections.includes(n.to));

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const open = () => setLoginOpen(true);
    window.addEventListener("open-login-modal", open);
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("open-login-modal", open);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card/70 backdrop-blur-xl supports-[backdrop-filter]:bg-card/60 border-b border-foreground/[0.08]">
        {/* Top row */}
        <div className="relative max-w-[1280px] mx-auto px-8 max-md:px-5 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex-shrink-0 flex items-center gap-1.5 group/logo overflow-hidden"
          >
            {logoOverride.light ? (
              <span className="relative h-5 flex items-center">
                <img
                  src={logoOverride.light}
                  alt="Logcomex"
                  className={cn(
                    "h-5 block",
                    logoOverride.dark ? "dark:hidden" : "dark:brightness-0 dark:invert"
                  )}
                  loading="eager"
                  decoding="async"
                />
                {logoOverride.dark && (
                  <img
                    src={logoOverride.dark}
                    alt="Logcomex"
                    className="h-5 hidden dark:block"
                    loading="eager"
                    decoding="async"
                  />
                )}
              </span>
            ) : (
              <span className="relative h-5 flex items-center">
                {/* Scrolled state logos */}
                <img src={logcomexAi4} alt="Logcomex.ai" width={796} height={122} className={`h-5 w-auto max-w-none transition-opacity duration-500 ease-in-out dark:brightness-0 dark:invert ${scrolled ? 'opacity-100 group-hover/logo:opacity-0' : 'opacity-0 pointer-events-none'}`} loading="eager" decoding="async" />
                <img src={logcomexAi3} alt="Logcomex.ai" width={796} height={122} className={`h-5 w-auto max-w-none absolute inset-0 transition-opacity duration-500 ease-in-out dark:brightness-0 dark:invert ${scrolled ? 'opacity-0 group-hover/logo:opacity-100' : 'opacity-0 pointer-events-none'}`} loading="eager" decoding="async" />
                {/* Top state logos */}
                <img src={logcomexLogo} alt="Logcomex" width={667} height={119} className={`h-5 w-auto max-w-none absolute inset-0 transition-opacity duration-500 ease-in-out dark:brightness-0 dark:invert ${!scrolled ? 'opacity-100 group-hover/logo:opacity-0' : 'opacity-0 pointer-events-none'}`} loading="eager" decoding="async" />
                <img src={logcomexAiIcon} alt="Logcomex.ai" width={796} height={122} className={`h-5 w-auto max-w-none absolute inset-0 transition-opacity duration-500 ease-in-out dark:brightness-0 dark:invert ${!scrolled ? 'opacity-0 group-hover/logo:opacity-100' : 'opacity-0 pointer-events-none'}`} loading="eager" decoding="async" />
              </span>
            )}
            {!scrolled && tagline && (
              <span className="relative flex items-center">
                <span className="transition-all duration-500 ease-in-out whitespace-nowrap overflow-hidden max-w-[200px] opacity-100 group-hover/logo:max-w-0 group-hover/logo:opacity-0">
                  <span className="text-sm lg:text-base font-light text-muted-foreground animate-[fadeSlideUp_1s_ease-out_0.3s_both]">{tagline}</span>
                </span>
              </span>
            )}
          </Link>

          <nav className="flex items-center gap-6 max-md:hidden">
            {PRIMARY_NAV.map((n) => {
              const active = pathname === n.to;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={cn(
                    "text-sm font-medium transition-colors",
                    active ? "text-primary" : "text-foreground/60 hover:text-primary"
                  )}
                >
                  {n.label}
                </NavLink>
              );
            })}
            <HeaderSearch />
            {showTheme && <ThemeToggle />}
            {false && showLang && <LanguageSwitcher />}
            
            
            <a href={headerCta.url} target={headerCta.external ? "_blank" : undefined} rel={headerCta.external ? "noopener noreferrer" : undefined}>
              <Button size="sm" variant="primary" iconRight={<ArrowRight />}>{headerCta.label}</Button>
            </a>
          </nav>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-2">
            {showTheme && <ThemeToggle />}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileMenuOpen}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md text-foreground/70 hover:text-primary hover:bg-muted transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Secondary row (desktop) */}
        <div className="border-t border-foreground/[0.06] max-md:hidden">
          <div className="relative max-w-[1280px] mx-auto px-8 max-md:px-5 h-11 flex items-center">
            <nav className="flex items-center gap-6 max-md:gap-4">
              {visibleNav.map((n) => {
                if (n.to === "/recursos") {
                  const active = pathname === "/recursos" || pathname.startsWith("/recursos/");
                  return <ResourcesMenu key={n.to + n.label} label={n.label} active={active} />;
                }
                if (n.external) {
                  return (
                    <a
                      key={n.to + n.label}
                      href={n.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[13px] font-medium transition-colors whitespace-nowrap text-foreground/65 hover:text-primary"
                    >
                      {n.label}
                    </a>
                  );
                }
                const active = n.to === "/" ? pathname === "/" : (pathname === n.to || pathname.startsWith(n.to + "/"));
                return (
                  <NavLink
                    key={n.to + n.label}
                    to={n.to}
                    className={cn(
                      "text-[13px] font-medium transition-colors whitespace-nowrap",
                      active ? "text-primary" : "text-foreground/65 hover:text-primary"
                    )}
                  >
                    {n.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-foreground/[0.06] bg-card/95 backdrop-blur-xl">
            <nav className="px-5 py-4 flex flex-col gap-1">
              {visibleNav.map((n) => {
                if (n.to === "/recursos") {
                  return (
                    <div key="recursos-mobile" className="flex flex-col">
                      {RESOURCE_LINKS.map((l, i) => (
                        <NavLink
                          key={l.to}
                          to={l.to}
                          className={cn(
                            "px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                            i > 0 && "pl-6 text-[13px]",
                            pathname === l.to
                              ? "text-primary bg-primary/5"
                              : "text-foreground/70 hover:text-primary hover:bg-muted",
                          )}
                        >
                          {i === 0 ? n.label : l.label}
                        </NavLink>
                      ))}
                    </div>
                  );
                }
                if (n.external) {
                  return (
                    <a
                      key={n.to + n.label}
                      href={n.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground/70 hover:text-primary hover:bg-muted transition-colors"
                    >
                      {n.label}
                    </a>
                  );
                }
                const active = n.to === "/" ? pathname === "/" : (pathname === n.to || pathname.startsWith(n.to + "/"));
                return (
                  <NavLink
                    key={n.to + n.label}
                    to={n.to}
                    className={cn(
                      "px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                      active ? "text-primary bg-primary/5" : "text-foreground/70 hover:text-primary hover:bg-muted"
                    )}
                  >
                    {n.label}
                  </NavLink>
                );
              })}
              <div className="border-t border-foreground/[0.08] mt-3 pt-3 flex flex-col gap-2">
                <div className="px-3 flex items-center gap-3">
                  <HeaderSearch />
                  {showTheme && <ThemeToggle />}
                  {false && showLang && <LanguageSwitcher />}
                </div>
                <div className="px-3 pt-1">
                  <a href={headerCta.url} target={headerCta.external ? "_blank" : undefined} rel={headerCta.external ? "noopener noreferrer" : undefined} className="block">
                    <Button size="sm" variant="primary" iconRight={<ArrowRight />} className="w-full justify-center">{headerCta.label}</Button>
                  </a>
                </div>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-[1280px] w-full mx-auto px-8 max-md:px-5 py-12 max-md:py-8">
        <Outlet />
      </main>

      <Footer />

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
